import {
  useWishlist,
  useWishlistProducts,
  useLocalWishlist,
  useCart,
  useAddItemsToCart,
  useCustomer,
} from 'hooks';
import { useEffect, useState } from 'react';

import CardItem from 'components/sections/highlights/cardItem';
import getShopifyData from 'lib/shopify/getShopifyData';
import SiteLink from 'components/shared/sitelink';
import { addLocaleToHref } from 'lib/utils/helpers';
import { useRouter } from 'next/router';
import { useSettings } from 'contexts/SettingContext';
import { getShopifyGid } from 'lib/shopify/shopifyGid';
import useQuickAddToCartTracking from 'hooks/tracking/useQuickAddToCartTracking';
import useProductAddedTracking from 'hooks/tracking/useProductAddedTracking';
import useIsMobile from 'hooks/utils/useIsMobile';
import trackWishlist from 'lib/utils/trackWishlist';

import { getSelectedSalonNo, getStorageCustomerSalonNo } from 'lib/shopify';
import styles from './wishlist.module.scss';

const Wishlist = ({ locale, globalModules }) => {
  const { wishlistProducts } = useWishlistProducts(locale);
  const { removeFromWishlist } = useWishlist();
  const { localWishlist } = useLocalWishlist();
  const [products, setProducts] = useState([]);
  const [shopifyData, setShopifyData] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const { cart } = useCart();
  const router = useRouter();
  const addItems = useAddItemsToCart();
  const { addProduct, setSaveItemSelectionList } = useSettings();
  const { customer } = useCustomer();
  const isMobile = useIsMobile();

  const salonNo = getStorageCustomerSalonNo(locale);
  const selectedSalonNo = getSelectedSalonNo(locale);
  const {
    freeItem,
    amountOfProductsToGetFreeItem,
    promoCollectionName,
    shopifyTagsToExcludeFromPromo,
  } = globalModules.secondpartsettings;
  const { wishlist: localizedTexts } = globalModules;
  const t = key => localizedTexts[key]?.text;

  useEffect(() => {
    const fetchAllProducts = async () => {
      const data = await getShopifyData({ productIds: localWishlist, locale });
      setShopifyData(data);
      setProducts(wishlistProducts);
    };
    fetchAllProducts();
  }, [wishlistProducts]);

  useEffect(() => {
    document.body.style.paddingBottom = isMobile && wishlistProducts.length > 0 ? '6rem' : '';
    return () => {
      document.body.style.paddingBottom = '';
    };
  }, [isMobile, wishlistProducts]);

  const handleAddAllToCart = async () => {
    const allProducts = shopifyData.reduce((prev, shopifyProduct) => {
      const relatedProducts = shopifyProduct.relatedProducts || [];
      return [...prev, shopifyProduct.variantId, ...relatedProducts];
    }, []);

    const productsDataForCheckout = [...new Set(allProducts)].map(id => ({
      variantId: id,
      quantity: 1,
    }));

    await addItems(productsDataForCheckout, freeItem?.product?.shopifyData, {
      amountOfProductsToGetFreeItem,
      promoCollectionName,
      shopifyTagsToExcludeFromPromo,
    });

    shopifyData.forEach((prod, i) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuickAddToCartTracking({
        product: {
          name: prod.productTitle,
          id: products[i].shopifyData.variants[0]?.product_id,
          productSpecs: products[i]?.productSpecs,
          price: prod.productPrice,
          index: i,
          quantity: 1,
        },
        list: `WishList`,
        setSaveItemSelectionList,
        locale,
      });
      // Universal Analytics
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useProductAddedTracking({
        affiliatedSalonNo: salonNo,
        selectedSalonNo,
        product: {
          name: prod.productTitle,
          id: products[i].shopifyData.variants[0]?.product_id,
          brand: 'Keune',
          category: products[i]?.productSpecs?.find(
            ({ type }) =>
              type === 'Producttype' || type === 'Type de produit' || type === 'Product type'
          )?.value,
          price: prod.productPrice,
          discount:
            prod.productCompareAtPrice !== null
              ? prod.productCompareAtPrice - prod.productPrice
              : null,
          quantity: 1,
        },
        location: { action: router },
      });

      if (!prod.isFreeItem) {
        addProduct({
          title: prod.productTitle,
          slug: prod.productHandle,
          variantId: getShopifyGid('ProductVariant', products[i].shopifyData.variants[0].id),
        });
      }
    });
  };

  const handleRemoveFromWishlist = async (product, index) => {
    const GID = getShopifyGid('Product', product.shopifyData.variants[0].product_id);
    await removeFromWishlist([GID]);
    trackWishlist(
      product,
      shopifyData[index],
      'remove_from_wishlist',
      `Category: Wish List`,
      index
    );
  };

  return (
    <section className={styles.wishlist}>
      <div className={styles.wishlistHeader}>
        <h1>{t('title')}</h1>
        {!customer?.isLoggedIn && (
          <div className={styles.loginRequest}>
            <p>{t('loginRequestText')}</p>
            <div className={styles.buttonsSection}>
              <SiteLink
                locale={locale}
                href={addLocaleToHref('/account/register/', locale, router)}
              >
                <button type="button" className={styles.buttonBlack}>
                  {t('buttonSignup')}
                </button>
              </SiteLink>
              <SiteLink locale={locale} href={addLocaleToHref('/account/login/', locale, router)}>
                <button type="button">{t('buttonLogin')}</button>
              </SiteLink>
            </div>
          </div>
        )}
      </div>

      {products?.length > 0 ? (
        <>
          <div className={styles.productsContainer}>
            {products?.map((product, index) => (
              <div className={styles.productWrapper} key={product.id}>
                <CardItem
                  locale={locale}
                  key={product.id}
                  itemData={product}
                  shopifyProduct={shopifyData[index]}
                  globalModules={globalModules}
                  itemIndex={index}
                  pageTitle="Wish List"
                />
                <div className={styles.removeProductContainer}>
                  <button
                    type="button"
                    className={styles.removeProductButton}
                    onClick={() => handleRemoveFromWishlist(product, index)}
                  >
                    {t('buttonRemoveProduct')}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.addAllProducts}>
            <button type="button" className={styles.buttonBlack} onClick={handleAddAllToCart}>
              {t('buttonAddAllProducts')}
            </button>
          </div>
        </>
      ) : (
        <div className={styles.emptyContainer}>
          <div className={styles.notification}>{t('emptyWishlistText')}</div>
          <SiteLink
            locale={locale}
            href={addLocaleToHref(
              globalModules?.settings?.slugEmptyCartButton?.link?.slug,
              locale,
              router
            )}
          >
            <button className={styles.checkoutButton} type="button">
              {t('buttonEmptyWishlist')}
            </button>
          </SiteLink>
        </div>
      )}
    </section>
  );
};

export default Wishlist;
